import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, MapPin, Crosshair, Send, CheckCircle2, Navigation, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationMap from "@/components/LocationMap";
import { api, fileUrl, eur } from "@/lib/api";

const empty = {
  customer_name: "", phone: "", email: "", customer_address: "", vehicle_model: "",
  license_plate: "", problem_description: "", address: "", dest_address: "",
};

const RequestForm = () => {
  const [form, setForm] = useState(empty);
  const [serviceType, setServiceType] = useState("defect");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [destCoords, setDestCoords] = useState({ lat: null, lng: null });
  const [photos, setPhotos] = useState([]); // {storage_path, original_filename}
  const [services, setServices] = useState([]);
  const [quote, setQuote] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/pricing").then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/quote", { service_type: serviceType, lat: coords.lat, lng: coords.lng, dest_lat: destCoords.lat, dest_lng: destCoords.lng })
        .then((r) => setQuote(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [serviceType, coords.lat, coords.lng, destCoords.lat, destCoords.lng]);

  const reverseGeocode = async (lat, lng, field) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await res.json();
      if (data.display_name) setForm((f) => ({ ...f, [field]: data.display_name }));
    } catch (e) { console.debug("reverse geocode failed", e); }
  };

  const onPickupChange = (lat, lng) => { setCoords({ lat, lng }); reverseGeocode(lat, lng, "address"); };
  const onDestChange = (lat, lng) => { setDestCoords({ lat, lng }); reverseGeocode(lat, lng, "dest_address"); };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Locatie niet ondersteund");
    toast.loading("Locatie ophalen...", { id: "geo" });
    navigator.geolocation.getCurrentPosition(
      (pos) => { toast.dismiss("geo"); onPickupChange(pos.coords.latitude, pos.coords.longitude); toast.success("Locatie gevonden"); },
      () => { toast.dismiss("geo"); toast.error("Kon locatie niet ophalen"); }
    );
  };

  const searchAddress = async (value, setter) => {
    if (!value.trim()) return;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=1`);
      const data = await res.json();
      if (data[0]) { setter({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }); toast.success("Adres gevonden op kaart"); }
      else toast.error("Adres niet gevonden");
    } catch (e) { toast.error("Zoeken mislukt"); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, res.data]);
      }
      toast.success(`${files.length} foto('s) geüpload`);
    } catch (err) {
      toast.error("Upload mislukt");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["customer_name", "phone", "email", "vehicle_model", "license_plate", "problem_description"]) {
      if (!form[k].trim()) return toast.error("Vul alle verplichte velden in");
    }
    setSubmitting(true);
    try {
      await api.post("/requests", { ...form, service_type: serviceType, lat: coords.lat, lng: coords.lng, dest_lat: destCoords.lat, dest_lng: destCoords.lng, photos });
      setDone(true);
      toast.success("Takelverzoek verzonden!");
    } catch (err) {
      toast.error("Verzenden mislukt, probeer opnieuw");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setForm(empty); setServiceType("defect"); setCoords({ lat: null, lng: null }); setDestCoords({ lat: null, lng: null }); setPhotos([]); setQuote(null); setDone(false); };

  if (done) {
    return (
      <div data-testid="request-success" className="rounded-2xl bg-slate-900/60 border border-slate-700/60 p-10 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-9 h-9 text-emerald-400" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-white mb-2">Verzoek ontvangen!</h3>
        <p className="text-slate-400 max-w-md mx-auto mb-6">Ons team neemt zo snel mogelijk contact met u op. Gemiddelde aankomsttijd is 25 minuten.</p>
        <Button onClick={reset} data-testid="new-request-btn" className="rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold">Nieuw verzoek indienen</Button>
      </div>
    );
  }

  const inputCls = "bg-slate-950/50 border-slate-700/60 text-white placeholder:text-slate-500 focus-visible:ring-sky-500";

  return (
    <form onSubmit={submit} data-testid="request-form" className="rounded-2xl bg-slate-900/60 border border-slate-700/60 p-6 sm:p-8 space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Naam *"><Input data-testid="input-customer-name" className={inputCls} value={form.customer_name} onChange={set("customer_name")} placeholder="Uw naam" /></Field>
        <Field label="Telefoonnummer *"><Input data-testid="input-customer-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+32 ..." /></Field>
      </div>
      <Field label="E-mailadres *"><Input data-testid="input-customer-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="naam@email.com" /></Field>
      <Field label="Adres klant (optioneel)"><Input data-testid="input-customer-address" className={inputCls} value={form.customer_address} onChange={set("customer_address")} placeholder="Straat, huisnummer, gemeente" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Merk & Model *"><Input data-testid="input-vehicle-model" className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="bijv. Volkswagen Golf" /></Field>
        <Field label="Kenteken *"><Input data-testid="input-license-plate" className={inputCls} value={form.license_plate} onChange={set("license_plate")} placeholder="1-ABC-123" /></Field>
      </div>
      <Field label="Probleem omschrijving *">
        <Textarea data-testid="input-problem-description" className={inputCls} rows={3} value={form.problem_description} onChange={set("problem_description")} placeholder="Beschrijf het probleem (motorstoring, klapband, ongeval, start niet...)" />
      </Field>

      <div className="space-y-1.5">
        <Label className="text-slate-300 flex items-center gap-1.5"><Wrench className="w-4 h-4 text-sky-400" /> Type dienst *</Label>
        <Select value={serviceType} onValueChange={setServiceType}>
          <SelectTrigger data-testid="input-service-type" className={inputCls}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-white">
            {services.map((s) => (
              <SelectItem key={s.key} value={s.key}>{s.label}{s.surcharge > 0 ? ` (+${eur(s.surcharge)})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Photos */}
      <div>
        <Label className="text-slate-300 mb-2 block">Foto's van het voertuig</Label>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="input-vehicle-photos" />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-photo-btn"
          className="w-full border-2 border-dashed border-slate-700 rounded-xl py-6 flex flex-col items-center gap-2 text-slate-400 hover:border-sky-500 hover:text-sky-400 transition-colors">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
          <span className="text-sm">{uploading ? "Uploaden..." : "Klik om foto's te uploaden"}</span>
        </button>
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3" data-testid="photo-previews">
            {photos.map((p, i) => (
              <div key={p.storage_path} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-700">
                <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-950/80 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ophaallocatie */}
      <div>
        <Label className="text-slate-300 mb-2 block flex items-center gap-1.5"><MapPin className="w-4 h-4 text-sky-400" /> Ophaallocatie (van)</Label>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <Input data-testid="input-address" className={inputCls} value={form.address} onChange={set("address")} placeholder="Voer ophaaladres in..." onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchAddress(form.address, setCoords))} />
          <Button type="button" onClick={() => searchAddress(form.address, setCoords)} data-testid="search-address-btn" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">Zoek</Button>
          <Button type="button" onClick={useMyLocation} data-testid="use-location-btn" className="bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 gap-1.5 border border-sky-500/30">
            <Crosshair className="w-4 h-4" /> Mijn locatie
          </Button>
        </div>
        <LocationMap lat={coords.lat} lng={coords.lng} onChange={onPickupChange} />
      </div>

      {/* Bestemming */}
      <div>
        <Label className="text-slate-300 mb-2 block flex items-center gap-1.5"><Navigation className="w-4 h-4 text-emerald-400" /> Bestemming (naar)</Label>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <Input data-testid="input-dest-address" className={inputCls} value={form.dest_address} onChange={set("dest_address")} placeholder="Bijv. garage of afleveradres..." onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchAddress(form.dest_address, setDestCoords))} />
          <Button type="button" onClick={() => searchAddress(form.dest_address, setDestCoords)} data-testid="search-dest-btn" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">Zoek</Button>
        </div>
        <LocationMap lat={destCoords.lat} lng={destCoords.lng} onChange={onDestChange} />
        <p className="text-xs text-slate-500 mt-2">Klik op de kaart of sleep de marker om de ophaal- en bestemmingslocatie te bepalen.</p>
      </div>

      {/* Prijsindicatie */}
      {quote && (
        <div data-testid="price-estimate" className="rounded-xl bg-sky-500/5 border border-sky-500/30 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="font-heading font-semibold text-white">Prijsindicatie</span>
            {quote.night_weekend && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-medium">Nacht/weekend</span>}
          </div>
          <div className="space-y-1.5 text-sm">
            <Row label="Voorrijkosten" value={eur(quote.base_callout)} />
            <Row label={`Afstand ${quote.distance_km} km (voorrijden ${quote.approach_km} + takelen ${quote.tow_km})${quote.distance_km > quote.km_threshold ? ` — ${eur(quote.per_km)}/km tot ${quote.km_threshold} km, daarna ${eur(quote.per_km_reduced)}/km` : ` × ${eur(quote.per_km)}/km`}`} value={eur(quote.km_cost)} />
            {quote.surcharge > 0 && <Row label="Toeslag dienst" value={eur(quote.surcharge)} />}
            {quote.night_weekend && <Row label="Nacht-/weekendtoeslag (50%)" value={eur(quote.night_extra)} />}
            <Row label="Subtotaal (excl. btw)" value={eur(quote.total)} />
            <Row label="Btw (21%)" value={eur(quote.vat)} />
            <div className="flex justify-between pt-2 mt-1 border-t border-sky-500/30 font-bold text-white text-base">
              <span>Totaal (incl. btw)</span><span data-testid="price-total" className="text-sky-400">{eur(quote.total_incl)}</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">Indicatieve prijs; de definitieve prijs kan afwijken op basis van de situatie ter plaatse.</p>
        </div>
      )}

      <Button type="submit" disabled={submitting} data-testid="submit-request-btn"
        className="w-full h-12 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-base gap-2 glow-blue">
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {submitting ? "Verzenden..." : "Takelverzoek verzenden"}
      </Button>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-300">{label}</Label>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4"><span className="text-slate-400">{label}</span><span className="text-slate-200 whitespace-nowrap">{value}</span></div>
);

export default RequestForm;
